import React from 'react';
import { WidgetWrapper } from '../../../../components/WidgetWrapper/WidgetWrapper';
import { EditField } from '../../../../components/EditPanel/types';
import { FollowUpQueriesWidgetProps, FollowUpQueriesWidgetState } from './types';
import { useWidgetEditable } from '../../../../hooks/useWidgetEditable';
import { FONT_WEIGHT_OPTIONS_NUMERIC } from '../../../../utils/fontWeightOptions';
import { themeColor } from '../../../../types/color';

const defaultState: FollowUpQueriesWidgetState = {
  // Pill styling (matching Figma design)
  pillBgColor: themeColor('background'),
  pillBgColorHover: themeColor('primary'),
  pillTextColor: themeColor('textPrimary'),
  pillTextColorHover: themeColor('background'),
  pillFontSize: 14,
  pillFontWeight: '500',
  pillBorderRadius: 100, // Pill shape
  pillPaddingX: 12,
  pillPaddingY: 12,

  // Border styling
  pillBorderWidth: 1,
  pillBorderColor: themeColor('primary'),
  pillBorderColorHover: themeColor('primary'),

  // Selected state styling
  pillBgColorSelected: themeColor('primary'),
  pillTextColorSelected: themeColor('secondary'),
  pillBorderColorSelected: themeColor('primary'),

  // Container
  containerGap: 8,
  maxWidthDesktop: '100%',
  maxWidthMobile: '100%',
};

const editFields: EditField[] = [
  // Default state styling
  { key: 'pillBgColor', label: 'Pill Background', type: 'color' },
  { key: 'pillBgColorHover', label: 'Pill Hover Background', type: 'color' },
  { key: 'pillTextColor', label: 'Pill Text Color', type: 'color' },
  { key: 'pillTextColorHover', label: 'Pill Hover Text Color', type: 'color' },

  // Border styling
  {
    key: 'pillBorderWidth',
    label: 'Border Width (px)',
    type: 'number',
    min: 0,
    max: 3,
    step: 1,
  },
  { key: 'pillBorderColor', label: 'Border Color', type: 'color' },
  { key: 'pillBorderColorHover', label: 'Border Hover Color', type: 'color' },

  // Selected state styling
  { key: 'pillBgColorSelected', label: 'Selected Background', type: 'color' },
  { key: 'pillTextColorSelected', label: 'Selected Text Color', type: 'color' },
  {
    key: 'pillBorderColorSelected',
    label: 'Selected Border Color',
    type: 'color',
  },

  // Typography
  {
    key: 'pillFontSize',
    label: 'Font Size (px)',
    type: 'number',
    min: 10,
    max: 18,
    step: 1,
  },
  {
    key: 'pillFontWeight',
    label: 'Font Weight',
    type: 'options',
    options: FONT_WEIGHT_OPTIONS_NUMERIC,
  },
  {
    key: 'pillBorderRadius',
    label: 'Border Radius (px)',
    type: 'number',
    min: 0,
    max: 100,
    step: 4,
  },
  {
    key: 'pillPaddingX',
    label: 'Horizontal Padding (px)',
    type: 'number',
    min: 4,
    max: 24,
    step: 2,
  },
  {
    key: 'pillPaddingY',
    label: 'Vertical Padding (px)',
    type: 'number',
    min: 2,
    max: 16,
    step: 2,
  },

  // Container
  {
    key: 'containerGap',
    label: 'Gap Between Pills (px)',
    type: 'number',
    min: 4,
    max: 16,
    step: 2,
  },
  {
    key: 'maxWidthDesktop',
    label: 'Max Width (Desktop)',
    type: 'options',
    options: [
      { label: '100%', value: '100%' },
      { label: '80%', value: '80%' },
      { label: '70%', value: '70%' },
      { label: '60%', value: '60%' },
    ],
  },
  {
    key: 'maxWidthMobile',
    label: 'Max Width (Mobile)',
    type: 'options',
    options: [
      { label: '100%', value: '100%' },
      { label: '90%', value: '90%' },
      { label: '80%', value: '80%' },
    ],
  },
];

export const FollowUpQueriesWidget: React.FC<FollowUpQueriesWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const { state, isLoading, isSaving, handleEditChange, handleSave, themeColors, resolveColor, canSave } =
    useWidgetEditable<FollowUpQueriesWidgetState>(widgetId, { ...defaultState, ...initialState }, editFields);

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Render the children with current state
  const widgetContent = children(state, resolveColor);

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
